import React, { useState, useEffect, useRef } from "react";
import { Row, Col, Button, Spin, message } from "antd";
import { v4 as uuidv4 } from "uuid";
import db from "../../../../database";
import { getCustomerCoupons, verifyCouponsService } from "../../../../services/index";
import useAuthStore from "../../../../stores/useAuthStore";
import useTransactionStore from "../../../../stores/useTransactionStore";
import messages from "../../../../errors/messages.en-IN.json";
import { useTranslation } from "react-i18next";
import * as Sentry from "@sentry/browser";
import { findPricingRuleById } from "../../../../utils/pricingRulesCache";

const CustomerCouponTab = ({
  cart,
  activeTabName,
  processCouponApplication,
  setDisplayAddNewCustomer,
  setDisplayEditOldCustomer,
  setAdditionalInfo,
  setShowCouponOtpPopup,
  setCouponVerificationData,
  loading,
  setLoading,
}) => {
  const { t } = useTranslation();
  const tillData = useAuthStore((s) => s.tillData);
  const [customerCouponsData, setCustomerCouponsData] = useState([]);
  const couponApplyInFlightRef = useRef(false);

  useEffect(() => {
    if (activeTabName !== "Coupons") return;
    if (!cart?.customer?.cwrCustomerId) return;
    if (cart.customer.cwrCustomerId === tillData?.tillAccess?.csBunit?.b2cCustomer?.cwrCustomerId) return;

    const fetchCoupons = async () => {
      setLoading(true);
      try {
        const response = await getCustomerCoupons(cart.customer.cwrCustomerId);
        if (response.status === 200) {
          const parsed = JSON.parse(response?.data?.data?.getCustomerCoupons);
          setCustomerCouponsData(parsed?.coupons || []);
        }
      } catch (error) {
        console.error("Error fetching customer coupons:", error);
      } finally {
        setLoading(false);
      }
    };
    fetchCoupons();
  }, [activeTabName]);

  const handleApplyCoupon = async (coupon) => {
    if (couponApplyInFlightRef.current) return;
    couponApplyInFlightRef.current = true;
    try {
    const existingCoupons = Array.isArray(cart?.couponInput) ? cart.couponInput.filter((item) => typeof item === "object" && (item?.couponCode || item?.mPricingCouponId)) : [];

    if (existingCoupons.length > 0) {
      message.warning("A coupon is already applied. Please remove the existing coupon to apply a new coupon.");

      return;
    }
    if (!Array.isArray(cart?.items) || cart.items.length === 0) {
      message.warning("Please add items to cart to apply a coupon.");

      return;
    }
    const isProductCoupon = coupon?.products?.length > 0;
    const eligibleItems = isProductCoupon ? cart.items.filter((item) => coupon.products.some((p) => p._id === item.productId)) : cart.items;
    const eligibleAmount = eligibleItems.reduce((sum, item) => sum + Number(item.nettotal || item.linegross || 0), 0);
    if (isProductCoupon && eligibleItems.length === 0) {
      message.warning("Coupon applicable only for selected products. Eligible items not found in cart.");
      return;
    }

    if (coupon?.min_bill_amount_for_redemption) {
      if (Number(cart?.total || 0) < Number(coupon.min_bill_amount_for_redemption || 0)) {
        message.warning(`Minimum bill amount ₹${coupon.min_bill_amount_for_redemption} required to apply this coupon`);
        return;
      }
    }
    setLoading(true);
    const uniqReferenceId = uuidv4().replace(/-/g, "").toUpperCase();
    const mobileNo = cart?.customer?.mobileNo || null;
    try {
      const data = {
        query: `query{
          verifyCoupons(couponcode:"${coupon?.coupon_code}",referenceId: "${uniqReferenceId}",bunitId: "${tillData.tillAccess.csBunit.csBunitId}",contactNo: ${mobileNo ? `"${mobileNo}"` : null}, pricingRuleId: ${coupon?.m_pricingrule_id ? `"${coupon?.m_pricingrule_id}"` : null}
          ){
            mPricingCouponId
            csClientId
            csBunitId
            created
            createdBy
            updated
            updatedBy
            line
            mPricingRulesId
            couponcode
            status
            redemptionCount
            usedDate
            upc
            otp
            additionalInfo {
              fieldName
              fieldType
              mandatory
              sequence
              displayLabel
              validationRule
            }
          }
        }`,
      };

      const verifyCouponResponse = await verifyCouponsService(data);
      const verifyCoupons = verifyCouponResponse?.data?.data?.verifyCoupons || [];

      if (verifyCoupons?.length > 0) {
        if (verifyCoupons[0].status === "A") {
          const matchingPricingRules = findPricingRuleById(verifyCoupons[0].mPricingRulesId);

          if (coupon?.discount_value) {
            if (coupon.discount_type === "percentage") {
              matchingPricingRules[0].percentageDiscount = coupon.discount_value;
              matchingPricingRules[0].discountType = "P";
            } else if (coupon.discount_type === "fixed") {
              matchingPricingRules[0].amountDiscount = coupon.discount_value;
              matchingPricingRules[0].discountType = "V";
            }
            if (coupon?.max_discount) {
              matchingPricingRules[0].maxDiscount = coupon.max_discount;
            }
            if (coupon?.products?.length > 0) {
              matchingPricingRules[0].applicableProducts = coupon.products;
              matchingPricingRules[0].eligibleAmount = eligibleAmount;
            }
            verifyCoupons[0].crmDiscountType = matchingPricingRules[0].discountType;
            verifyCoupons[0].crmPercentageDiscount = matchingPricingRules[0].percentageDiscount;
            verifyCoupons[0].crmAmountDiscount = matchingPricingRules[0].amountDiscount;
          }

          matchingPricingRules[0].iscoupon = "Y";

          if (verifyCoupons[0]?.additionalInfo?.length > 0) {
            setAdditionalInfo(verifyCoupons[0].additionalInfo);
            return;
          }

          if (coupon?.otpFlag === "Y") {
            setShowCouponOtpPopup(true);
            setCouponVerificationData({
              verifyCoupons: verifyCoupons[0],
              pricingRules: matchingPricingRules[0],
              uniqReferenceId,
            });
          } else {
            await processCouponApplication(verifyCoupons[0], matchingPricingRules, uniqReferenceId);
            setDisplayAddNewCustomer(false);
            setDisplayEditOldCustomer(false);
          }
        } else {
          message.warning(`${messages.errors.coupons.coupon_expired}`);
        }
      } else {
        Sentry.captureException(new Error("Coupon code failed"), {
          extra: { couponcode: coupon?.coupon_code, referenceId: uniqReferenceId },
        });
        message.warning(`${t("coupon_error")}`);
      }
    } catch (error) {
      console.debug(error);
    } finally {
      setLoading(false);
    }
    } finally {
      couponApplyInFlightRef.current = false;
    }
  };

  if (activeTabName !== "Coupons") return null;

  return (
    <div style={{ width: "100%", overflowX: "hidden" }}>
      {loading ? (
        <div style={{ textAlign: "center", padding: "40px" }}>
          <Spin />
        </div>
      ) : (
        <Row gutter={[16, 16]} style={{ marginTop: "20px", marginBottom: "12px" }}>
          {customerCouponsData?.length === 0 && (
            <Col span={24}>
              <div style={{ textAlign: "center", color: "#9ca3af", padding: "40px 0" }}>No coupons available</div>
            </Col>
          )}
          {customerCouponsData?.map((coupon, index) => (
            <Col xs={24} sm={24} md={12} lg={12} key={index}>
              <div
                style={{
                  border: "1px solid #e5e7eb",
                  borderRadius: "8px",
                  padding: "14px 16px",
                  background: "#fff",
                  display: "flex",
                  flexDirection: "column",
                  gap: "6px",
                  minHeight: "110px",
                  justifyContent: "space-between",
                }}
              >
                <div>
                  <div style={{ fontWeight: 600, fontSize: "15px", color: "#1f2937" }}>{coupon?.coupon_name}</div>
                  <div style={{ fontSize: "13px", color: "#6b7280", marginTop: "2px" }}>
                    Provider: <span style={{ fontWeight: 500 }}>{coupon.provider}</span>
                  </div>
                  <div style={{ fontSize: "13px", color: "#6b7280", marginTop: "2px" }}>
                    Code: <span style={{ fontWeight: 500 }}>{coupon?.coupon_code}</span>
                  </div>
                  <div style={{ fontSize: "13px", color: "#374151", marginTop: "2px" }}>
                    Discount: {coupon?.discount_type === "percentage" ? `${coupon?.discount_value}%` : `₹${coupon?.discount_value}`}
                  </div>
                </div>
                <div style={{ display: "flex", justifyContent: "flex-end", marginTop: "6px" }}>
                  <Button
                    size="small"
                    disabled={loading}
                    style={{
                      textAlign: "center",
                      display: "flex",
                      alignItems: "center",
                      justifyContent: "center",
                      padding: "6px 16px",
                      background: "#2F3856",
                      height: "5vh",
                      color: "#fff",
                      border: "none",
                      borderRadius: "6px",
                      fontWeight: 500,
                      cursor: "pointer",
                    }}
                    onClick={() => handleApplyCoupon(coupon)}
                  >
                    Apply
                  </Button>
                </div>
              </div>
            </Col>
          ))}
        </Row>
      )}
    </div>
  );
};

export default CustomerCouponTab;
